import { type AnalysisType, type PropertyType } from "@/generated/graphql"
import { sortOptions } from "@/types/compTypes"
import type { AnalysesSortOption, SortDirectionOption } from "@/types"

// Define a type that restricts the possible return values to specific strings
type AnalysisReturnType = "sourced" | "shared" | "analyzed"

// Array of valid statuses
const VALID_STATUSES = ["active", "pending", "contingent", "unknown"] as const
type StatusReturnType = (typeof VALID_STATUSES)[number]

/**
 * Gets the analysis type based on the properties of the AnalysisType object.
 * To be used throughout Analysis Tracking to handle types of analyses as they evolve.
 *
 * @param analysis The analysis object of type AnalysisType.
 * @returns A string representing the type of the analysis.
 */
export const getAnalysisType = (
  analysis: Pick<AnalysisType, "id" | "isSourced" | "sharedFrom">
): AnalysisReturnType => {
  if (analysis.isSourced) {
    return "sourced"
  } else if (analysis.sharedFrom) {
    return "shared"
  } else {
    return "analyzed"
  }
}

/**
 * Gets the property status on an analysis based on the status of the subject property.
 * To be used throughout Analysis Tracking to handle status reporting.
 *
 * @param analysis The analysis object of type AnalysisType.
 * @returns A string representing the property's status.
 */
export const getPropertyStatus = (
  analysis: Pick<AnalysisType, "id" | "property">
): StatusReturnType => {
  const property: PropertyType = analysis.property
  const normalizedStatus = property.status ? property.status.toLowerCase() : ""

  if (normalizedStatus && isValidStatus(normalizedStatus)) {
    return normalizedStatus as StatusReturnType
  } else {
    return "unknown" // Return 'unknown' if the status is not valid or undefined
  }
}

// Narrowing function to check if a string is a valid status
function isValidStatus(status: string): status is StatusReturnType {
  return VALID_STATUSES.includes(status as StatusReturnType)
}

/**
 * Gets the sort section configuration based on the sort criterion.
 * @param criterion
 */
export const getSortButtonTitle = (criterion: string) => {
  for (const [label, option] of Object.entries(sortOptions)) {
    if (option.criterion === criterion) {
      return label // Return the label if the criterion matches
    }
  }
  return "Sort" // Default title if no matching criterion is found
}

/**
 * Combines the sort criterion and direction into a single string for making the request.
 * When reversing the sort direction, a "-" is prepended to the criterion.
 *
 * @param sort - The sort criterion.
 * @param direction - The sort direction, either "default" or "reverse".
 * @returns A string representing the combined sort criterion and direction.
 */
export const combineSortAndDirection = (
  sort: AnalysesSortOption,
  direction: SortDirectionOption
) => {
  // Force "updated_at" to use "reverse" (newest first) by default
  // EN-4853: TODO: This really should be handled in the backend as 'reverse' is the opposite of 'default'
  // which should be what's initially sent by the server
  if (sort === "updated_at" && direction === "default") {
    return `-${sort}`
  } else if (sort === "updated_at") {
    return sort
  }
  return `${direction === "reverse" ? "-" : ""}${sort}`
}

/**
 * Gets the ordering labels for the analysis list based on the sort criterion.
 * @param criterion
 */
export const getOrderingLabels = (criterion: string | null) => {
  if (!criterion) {
    return null
  }
  switch (criterion) {
    case "updated_at":
      return { ascending: "Newest to Oldest", descending: "Oldest to Newest" }
    case "soldDate":
      return { ascending: "Oldest to Newest", descending: "Newest to Oldest" }
    case "profit":
    case "after_repair_value":
    case "purchase_price":
      return { ascending: "Low to High", descending: "High to Low" }
    case "bedrooms":
    case "baths":
    case "squareFeet":
      return { ascending: "Fewest to Most", descending: "Most to Fewest" }
    case "year_built":
      return { ascending: "Oldest to Newest", descending: "Newest to Oldest" }
    case "source_email":
    case "is_sourced":
      return { ascending: "Low to High", descending: "High to Low" }
    default:
      return { ascending: "Ascending", descending: "Descending" }
  }
}
